import React, { useState } from 'react';
import { Box, Typography, Container, Grid, Button, Card, CardContent, Stack, Collapse } from '@mui/material';
import LandingNavbar from './LandingPageNavBar';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import { useNavigate } from 'react-router-dom';

const featureCards = [
  {
    icon: <HealthAndSafetyIcon fontSize="large" />,
    title: 'Specialist supported living',
    text: 'Tailored 24-hour support within community-based accommodation so adults with complex needs can live safely at home while remaining close to loved ones.',
  },
  {
    icon: <SupervisorAccountIcon fontSize="large" />,
    title: 'Registered residential care',
    text: 'Highly trained staff provide medication management, personal care, meal preparation, and social engagement inside CQC-regulated homes.',
  },
  {
    icon: <Diversity3Icon fontSize="large" />,
    title: 'Outreach & domiciliary care',
    text: 'Flexible 1:1 visits supporting daily living, vocational goals, and integration with education or employment pathways across Hertfordshire and London.',
  },
];

const locations = [
  {
    title: 'Letchworth Office',
    address: ['70A Leys Avenue', 'Letchworth', 'Hertfordshire', 'England', 'SG6 3EG'],
    officePhone: '01462 684757',
    mobile: '07772 943653',
  },
  {
    title: 'Glasgow Office',
    address: ['11 Dalrymple Court', 'Kirkintilloch', 'Glasgow', 'Scotland', 'G66 3AA'],
    officePhone: '0141 776 6598',
    mobile: '07956 114564',
  },
  {
    title: 'Paisley Office',
    address: ['72 Causyside Street', 'Paisley', 'Renfrewshire', 'Scotland', 'PA1 1YP'],
    officePhone: '0141 889 5133',
    mobile: '07984 332090',
  },
  {
    title: 'Lancaster Office',
    address: ['1-2-8 The Barracks', 'White Cross IND Estate', 'South Road', 'Lancaster', 'Lancashire', 'England', 'LA1 4XQ'],
    officePhone: '01524 413308',
    mobile: '07958 708718',
  },
  {
    title: 'Edinburgh Office',
    address: ['134 North High Street', 'Musselburgh', 'East Lothian', 'EH21 6AS'],
    officePhone: '0131 285 6795',
    mobile: '07951 379434',
  },
  {
    title: 'Galston Office',
    address: ['1 Polwarth Street', 'Galston', 'Scotland', 'KA4 8HG'],
    officePhone: '01563 593673',
  },
  {
    title: 'Colchester Office',
    address: ['Weston (Business Centres) Ltd', 'Hawkins Road', 'Colchester', 'Essex', 'England', 'CO2 8JX'],
    officePhone: '01206 489881',
    mobile: '07572 086867',
  },
  {
    title: 'Irvine Office',
    address: ['153 High Street', 'Irvine', 'North Ayrshire', 'Scotland', 'KA12 8AD'],
    officePhone: '01294 442018',
    mobile: '07572 224035',
  },
  {
    title: 'Falkirk / Stirling Office',
    address: ['86 Manor Street', 'Falkirk', 'Scotland', 'FK1 1NU'],
    officePhone: '01324 461076',
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [expandedFeature, setExpandedFeature] = useState(null);

  return (
    <>
      <LandingNavbar />
      <Box
        sx={{
          bgcolor: 'rgba(142,81,255,0.85)',
          color: '#fff',
          pt: { xs: 16, md: 20 },
          pb: { xs: 10, md: 14 },
          px: 3,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" fontWeight={700} gutterBottom>
            Delight Supported Living
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, mb: 4 }}>
            Delivering person-centred residential, supported living, and outreach services
            for adults with learning disabilities, autism, mental health needs, and behaviours that challenge.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="center" spacing={2}>
            <Button variant="contained" size="large" onClick={() => navigate('/register')}>
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                color: '#000',
                borderColor: '#000',
                '&:hover': { borderColor: '#000', backgroundColor: 'rgba(0,0,0,0.04)' },
              }}
            >
              Contact us
            </Button>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ pt: 16, pb: 12, bgcolor: '#f5f6fb' }}>
        <Container maxWidth="lg">
          <Typography textAlign="center" variant="h5" fontWeight={600} mb={6}>
            Trusted by families, local authorities, and clinical commissioners across the UK
          </Typography>
          <Grid container spacing={4}>
            {featureCards.map((card, idx) => (
              <Grid item xs={12} md={4} key={card.title}>
                <Card
                  onClick={() => setExpandedFeature(expandedFeature === idx ? null : idx)}
                  sx={{
                    p: 3,
                    borderRadius: 5,
                    minHeight: 200,
                    maxWidth: 360,
                    boxShadow: '0 20px 35px rgba(11,13,33,0.08)',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    mx: 'auto',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ color: '#8E51FF', mb: 2 }}>{card.icon}</Box>
                    <Typography variant="h6" fontWeight={700}>
                      {card.title}
                    </Typography>
                    <Collapse in={expandedFeature === idx}>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {card.text}
                      </Typography>
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: 10, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
  
          <Typography textAlign="center" variant="h4" fontWeight={700} mb={4}>
            Our Locations
          </Typography>
          <Typography textAlign="center" color="text.secondary" sx={{ mb: 6 }}>
            From our Hitchin headquarters, Delight Supported Living provides specialist homes, step-down services,
            and outreach support across England and Scotland.
          </Typography>
          <Grid container spacing={4}>
            {locations.map((location) => (
              <Grid item xs={12} md={4} key={location.title}>
                <Card sx={{ borderRadius: 4, p: 3, height: '100%', boxShadow: '0 25px 40px rgba(11,13,33,0.08)' }}>
                  <Typography variant="h6" fontWeight={700}>
                    {location.title}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {location.address.map((line) => (
                      <Typography key={line} variant="body2" color="text.secondary">
                        {line}
                      </Typography>
                    ))}
                  </Box>
                  {location.officePhone && (
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      <strong>Office:</strong> {location.officePhone}
                    </Typography>
                  )}
                  {location.mobile && (
                    <Typography variant="body2">
                      <strong>Mobile:</strong> {location.mobile}
                    </Typography>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box textAlign="center" mt={6}>
            <Button variant="outlined" onClick={() => navigate('/contact')}>
              Contact our regional team
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default LandingPage;
